import {
	ChevronRight,
	Copy,
	Download,
	RefreshCcw,
	Send,
	Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { Page } from '#app/components/page.tsx'
import { Accordion } from '#app/components/ui/accordion.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { BIN_ID_STORAGE_KEY } from '#app/domains/mocra/sync/jsonbin.client.ts'
import { type Route } from '#app/routes/mocra+/_form+/+types/generate.ts'
import { ProtectedPage } from '#app/routes/mocra+/_form+/components/protected-page.tsx'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import {
	hasDocxTemplate,
	renderDocxTemplate,
} from '#app/routes/mocra+/_form+/generate/docx-templates.ts'
import {
	type IntakeDraft,
	loadIntakeDraft,
} from '#app/routes/mocra+/_form+/generate/intake-loader.ts'
import {
	GENERATE_SECTION_RESOURCES,
	computeSectionStatuses,
} from '#app/routes/mocra+/_form+/generate/sections.controller.ts'
import {
	renderTemplates,
	type RenderedTemplate,
	type TemplateId,
} from '#app/routes/mocra+/_form+/generate/summary.controller.ts'
import { createTarArchive } from '#app/routes/mocra+/_form+/generate/tar.ts'
import { clearAllSections } from '#app/routes/mocra+/_form+/intake-storage.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Generate Package' },
]

export default function GeneratePackage() {
	const [draft, setDraft] = useState<IntakeDraft>({})
	const [copiedTemplateId, setCopiedTemplateId] = useState<TemplateId | null>(
		null,
	)
	const [copyError, setCopyError] = useState<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const refreshDraft = useCallback(() => {
		setDraft(loadIntakeDraft())
	}, [])

	useEffect(() => {
		if (typeof window === 'undefined') return
		refreshDraft()
		const handleStorage = (event: StorageEvent) => {
			if (!event.key || event.key === 'mocra-intake') {
				refreshDraft()
			}
		}
		window.addEventListener('storage', handleStorage)
		return () => window.removeEventListener('storage', handleStorage)
	}, [refreshDraft])

	const sectionStatuses = useMemo(() => computeSectionStatuses(draft), [draft])

	const generatedTimestamp = useMemo(() => new Date(), [])

	const generatedAtLabel = useMemo(
		() =>
			generatedTimestamp.toLocaleString(undefined, {
				dateStyle: 'medium',
				timeStyle: 'short',
			}),
		[generatedTimestamp],
	)

	const generatedTemplates = useMemo<RenderedTemplate[]>(
		() => renderTemplates(draft, generatedAtLabel),
		[draft, generatedAtLabel],
	)

	const allSectionsComplete = sectionStatuses.every(
		(section) => section.complete,
	)

	const handleCopy = useCallback(async (template: RenderedTemplate) => {
		if (typeof navigator === 'undefined' || !navigator.clipboard) {
			setCopyError(
				'Clipboard copying is not available. Download the markdown file instead.',
			)
			return
		}
		try {
			await navigator.clipboard.writeText(template.content)
			setCopyError(null)
			setCopiedTemplateId(template.id)
			toast.success('Copied to clipboard')
			window.setTimeout(() => {
				setCopiedTemplateId((current) =>
					current === template.id ? null : current,
				)
			}, 2000)
		} catch {
			setCopyError(
				'Copy failed—please use the download button to retrieve the markdown.',
			)
		}
	}, [])

	const handleDownload = useCallback((template: RenderedTemplate) => {
		if (typeof window === 'undefined') return
		const blob = new Blob([template.content], { type: 'text/markdown' })
		const url = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = template.filename
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url)
	}, [])

	const handleDownloadAll = useCallback(() => {
		if (typeof window === 'undefined' || generatedTemplates.length === 0) return
		const tarBlob = createTarArchive(
			generatedTemplates.map((template) => ({
				name: template.filename,
				content: template.content,
			})),
		)
		const url = URL.createObjectURL(tarBlob)
		const link = document.createElement('a')
		link.href = url
		link.download = 'mocra-sops.tar'
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url)
	}, [generatedTemplates])

	const handleDownloadDocx = useCallback(
		async (templateId: TemplateId) => {
			try {
				if (typeof window === 'undefined') return
				const result = await renderDocxTemplate(
					templateId,
					draft,
					generatedTimestamp,
				)
				const url = URL.createObjectURL(result.blob)
				const link = document.createElement('a')
				link.href = url
				link.download = result.filename
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				URL.revokeObjectURL(url)
			} catch (error) {
				console.error('Failed to generate DOCX template', error)
				toast.error('Unable to generate the Word document. Please try again.')
			}
		},
		[draft, generatedTimestamp],
	)

	const handleRefreshClick = () => {
		refreshDraft()
		setCopyError(null)
		setCopiedTemplateId(null)
	}

	const handleClearAllDrafts = useCallback(() => {
		if (typeof window === 'undefined') return
		const confirmed = window.confirm(
			'This will remove all saved MoCRA intake answers. This action cannot be undone. Clear everything?',
		)
		if (!confirmed) return
		clearAllSections()
		refreshDraft()
		setCopyError(null)
		setCopiedTemplateId(null)
		toast.success('All MoCRA intake drafts cleared.')
	}, [refreshDraft])

	const handleSubmitToJsonBin = useCallback(async () => {
		if (!allSectionsComplete) {
			toast.error('Please complete all sections before submitting.')
			return
		}

		setIsSubmitting(true)
		try {
			let binId: string | undefined
			try {
				if (typeof window !== 'undefined') {
					const stored = window.localStorage.getItem(BIN_ID_STORAGE_KEY)
					binId = stored || undefined
				}
			} catch {}

			const response = await fetch('/api/mocra/submit', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ ...draft, binId }),
			})

			const result = (await response.json()) as {
				error?: string
				binId?: string
			}

			if (!response.ok) {
				throw new Error(result.error || 'Failed to submit')
			}

			toast.success('Submission saved successfully!')
			if (result.binId && typeof window !== 'undefined') {
				try {
					window.localStorage.setItem(BIN_ID_STORAGE_KEY, result.binId)
				} catch {}
			}
		} catch (error) {
			console.error('Submission error:', error)
			toast.error(
				error instanceof Error
					? error.message
					: 'Failed to submit. Please try again.',
			)
		} finally {
			setIsSubmitting(false)
		}
	}, [draft, allSectionsComplete])

	return (
		<ProtectedPage>
			<Page
				title="Generate Package"
				description="Review the intake sections, then copy or download MoCRA (Modernization of Cosmetics Regulation Act) SOP (standard operating procedure) drafts populated with your answers."
				className="space-y-8"
			>
				<Divider />
				<Section
					title="Intake completeness"
					description={
						allSectionsComplete
							? 'Every section is ready to merge. Download the SOP drafts below for review.'
							: 'Finish the sections flagged below to strengthen the generated SOPs before sharing them with your team.'
					}
					mocraSections={GENERATE_SECTION_RESOURCES.mocraSections}
					templates={GENERATE_SECTION_RESOURCES.templates}
				>
					<ul className="space-y-3">
						{sectionStatuses.map((section) => (
							<li key={section.key}>
								<Link
									to={section.route}
									className="border-border underlin-offset-2 flex items-center justify-between gap-4 rounded-md border px-4 py-3 hover:underline"
								>
									<div className="text-foreground text-sm font-medium">
										{section.label}
									</div>
									<div className="flex items-center gap-1.5">
										<Badge variant={section.complete ? 'success' : 'warning'}>
											{section.complete ? 'Complete' : 'Needs attention'}
										</Badge>
										<ChevronRight className="h-4 w-4" />
									</div>
								</Link>
							</li>
						))}
					</ul>
				</Section>

				<Divider />
				<Section
					title="Generated SOPs"
					description="The preamble on each file summarizes the inputs you provided so your team can validate before finalizing the SOP."
					mocraSections={GENERATE_SECTION_RESOURCES.mocraSections}
					templates={GENERATE_SECTION_RESOURCES.templates}
					meta={
						<div className="flex flex-wrap gap-2 pt-3">
							<Button
								type="button"
								size="sm"
								onClick={handleSubmitToJsonBin}
								disabled={!allSectionsComplete || isSubmitting}
							>
								<Send className="mr-2 h-4 w-4" />
								{isSubmitting ? 'Submitting...' : 'Submit to JSONBin'}
							</Button>
							<Button type="button" size="sm" onClick={handleDownloadAll}>
								<Download className="mr-2 h-4 w-4" />
								Download all (.tar)
							</Button>
							<Button variant="outline" size="sm" onClick={handleRefreshClick}>
								<RefreshCcw className="mr-2 h-4 w-4" />
								Refresh
							</Button>
							<Button
								type="button"
								variant="destructive"
								size="sm"
								onClick={handleClearAllDrafts}
							>
								<Trash2 className="mr-2 h-4 w-4" />
								Clear saved answers
							</Button>
						</div>
					}
					childrenClassName="space-y-4"
				>
					{copyError ? (
						<p className="text-destructive text-sm">{copyError}</p>
					) : null}
					<div className="space-y-3">
						{generatedTemplates.map((template) => (
							<Accordion
								key={template.id}
								triggerContent={
									<p className="text-foreground text-sm font-medium">
										{template.title}
									</p>
								}
								meta={
									copiedTemplateId === template.id ? (
										<span className="text-primary text-xs">Copied</span>
									) : null
								}
								containerClassName="border border-border"
								contentInnerClassName="space-y-3"
							>
								<div className="flex flex-wrap gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => handleCopy(template)}
									>
										<Copy className="mr-2 h-4 w-4" />
										Copy
									</Button>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => handleDownload(template)}
									>
										<Download className="mr-2 h-4 w-4" />
										Download Markdown
									</Button>
									{hasDocxTemplate(template.id) ? (
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() => handleDownloadDocx(template.id)}
										>
											<Download className="mr-2 h-4 w-4" />
											Download Word
										</Button>
									) : null}
								</div>
								<pre className="border-border bg-muted/40 text-foreground max-h-[420px] overflow-auto rounded-md border p-3 text-xs leading-relaxed whitespace-pre-wrap">
									{template.content}
								</pre>
							</Accordion>
						))}
					</div>
				</Section>
			</Page>
		</ProtectedPage>
	)
}
